import axios from 'axios';
import { toast } from 'sonner';

export const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
export const API_BASE = `${BACKEND_URL}/api`;

const DEFAULT_TIMEOUT_MS = 120000;

const getErrorDetail = (error) => {
  if (!error) return '';
  const responseData = error.response?.data;
  if (typeof responseData === 'string' && responseData.trim()) return responseData.trim();
  if (typeof responseData?.detail === 'string' && responseData.detail.trim()) return responseData.detail.trim();
  if (typeof responseData?.error === 'string' && responseData.error.trim()) return responseData.error.trim();
  if (typeof error.message === 'string' && error.message.trim()) return error.message.trim();
  return 'The engine returned an unexpected response.';
};

export const describeApiError = (error, fallbackAction = 'finish that step') => {
  const status = Number(error?.status || error?.response?.status || 0);
  const code = String(error?.code || '').toUpperCase();
  const detail = getErrorDetail(error);
  const lowered = detail.toLowerCase();

  if (
    lowered.includes('api key')
    || lowered.includes('openrouter')
    || lowered.includes('google ai key')
    || lowered.includes('google key')
    || lowered.includes('no usable api key')
    || lowered.includes('no api key')
  ) {
    return {
      title: 'Dr. Wold needs an engine key',
      description: 'The AI module is not armed yet. Add a Google or OpenRouter key in Settings, then try again.',
    };
  }

  if (code === 'ECONNABORTED' || lowered.includes('timeout')) {
    return {
      title: 'Dr. Wold is still thinking',
      description: 'That module took too long to answer. Try again, or narrow the scope and let the engine breathe.',
    };
  }

  if (lowered.includes('network error') || lowered.includes('backend unreachable') || lowered.includes('fetch failed')) {
    return {
      title: 'Dr. Wold lost the line',
      description: 'The frontend cannot reach the backend right now. Check the local engine and retry.',
    };
  }

  if (status === 404) {
    return {
      title: 'Dr. Wold could not find that record',
      description: detail || 'The requested analysis, chart, or report is no longer available.',
    };
  }

  if (status === 409) {
    return {
      title: 'Dr. Wold hit a state conflict',
      description: detail || 'The engine rejected that action because the current state changed underneath it.',
    };
  }

  if (status >= 500) {
    return {
      title: 'Dr. Wold found a backend snag',
      description: detail || 'The server tripped while trying to complete that request. A retry is worth trying.',
    };
  }

  if (status >= 400) {
    return {
      title: 'Dr. Wold wants one small correction',
      description: detail || `The engine needs a cleaner request to ${fallbackAction}.`,
    };
  }

  return {
    title: 'Dr. Wold hit a snag',
    description: detail || `The engine could not ${fallbackAction}.`,
  };
};

const normalizeApiError = (error) => {
  const detail = getErrorDetail(error);
  const normalized = error instanceof Error ? error : new Error(detail);
  const friendly = describeApiError(error);

  normalized.message = detail || friendly.description;
  normalized.status = Number(error?.response?.status || error?.status || 0);
  normalized.code = error?.code;
  normalized.response = error?.response;
  normalized.config = error?.config;
  normalized.friendlyTitle = friendly.title;
  normalized.friendlyDescription = friendly.description;
  normalized.userMessage = `${friendly.title}. ${friendly.description}`;
  return normalized;
};

export const showFriendlyApiError = (error, fallbackAction = 'finish that step', options = {}) => {
  const normalized = error?.friendlyTitle ? error : normalizeApiError(error);
  const friendly = error?.friendlyTitle
    ? { title: error.friendlyTitle, description: error.friendlyDescription }
    : describeApiError(normalized, fallbackAction);

  toast.error(friendly.title, {
    id: options.id,
    description: friendly.description,
    duration: options.duration ?? 6500,
  });

  return normalized;
};

export const api = axios.create({
  baseURL: API_BASE,
  timeout: DEFAULT_TIMEOUT_MS,
});

api.interceptors.response.use(
  (response) => response,
  (error) => Promise.reject(normalizeApiError(error)),
);

const asData = (request) => request.then((response) => response.data);

export const getAnalysisChartUrl = (
  analysisId,
  {
    view = 'annotated',
    candidateId,
    candidateIndex = 0,
    showFibs = true,
    showTimeframe = false,
    predictionSteps = 0,
    presentationMode,
  } = {},
) => {
  const params = new URLSearchParams();
  params.set('view', view);
  params.set('candidate_index', String(candidateIndex));
  params.set('show_fibs', String(Boolean(showFibs)));
  params.set('show_timeframe', String(Boolean(showTimeframe)));
  params.set('prediction_steps', String(predictionSteps || 0));
  if (candidateId) params.set('candidate_id', candidateId);
  if (presentationMode) params.set('presentation_mode', presentationMode);
  return `${API_BASE}/analyses/${analysisId}/chart?${params.toString()}`;
};

export const createProject = (data) => asData(api.post('/projects', data));
export const listProjects = () => asData(api.get('/projects'));
export const getProject = (id) => asData(api.get(`/projects/${id}`));
export const deleteProject = (id) => asData(api.delete(`/projects/${id}`));

export const uploadImage = (
  projectId,
  file,
  { userPivots = [], uploadSource = 'file_upload', analysisFocus = 'macro', analysisSpan = null } = {},
) => {
  const form = new FormData();
  form.append('file', file);
  form.append('upload_source', uploadSource);
  form.append('analysis_focus', analysisFocus || 'macro');
  if (Array.isArray(userPivots)) form.append('user_pivots', JSON.stringify(userPivots));
  if (analysisSpan && typeof analysisSpan === 'object') {
    form.append('analysis_span', JSON.stringify(analysisSpan));
  }
  return asData(api.post(`/projects/${projectId}/uploads`, form, {
    headers: { 'Content-Type': 'multipart/form-data' },
  }));
};

export const submitLivePivots = (projectId, pivots) => asData(api.post(`/projects/${projectId}/live-pivots`, pivots));

export const getJob = (jobId) => asData(api.get(`/jobs/${jobId}`));

export const pollJob = (jobId, onProgress, intervalMs = 450) => new Promise((resolve, reject) => {
  const poll = async () => {
    try {
      const job = await getJob(jobId);
      onProgress?.(job);
      if (job.status === 'completed') {
        resolve(job);
        return;
      }
      if (job.status === 'failed') {
        reject(new Error(job.error || 'Job failed'));
        return;
      }
      setTimeout(poll, intervalMs);
    } catch (error) {
      reject(error);
    }
  };

  poll();
});

export const getAnalysis = (id) => asData(api.get(`/analyses/${id}`));

export const preferCandidate = (analysisId, candidateId) => {
  const form = new FormData();
  form.append('candidate_id', candidateId);
  return asData(api.post(`/analyses/${analysisId}/prefer`, form));
};

export const updatePivot = (analysisId, candidateId, pivotIndex, label, locked) => {
  const form = new FormData();
  form.append('candidate_id', candidateId);
  form.append('pivot_index', pivotIndex);
  if (label !== undefined) form.append('label', label);
  if (locked !== undefined) form.append('locked', locked);
  return asData(api.post(`/analyses/${analysisId}/update-pivot`, form));
};

export const getChartMetadata = (analysisId, { candidateId, candidateIndex = 0 } = {}) => asData(api.get(
  `/analyses/${analysisId}/chart-metadata`,
  {
    params: {
      candidate_id: candidateId,
      candidate_index: candidateIndex,
    },
  },
));

export const rescoreAnalysis = (analysisId, payload) => asData(api.post(`/analyses/${analysisId}/rescore`, payload));

export const getStrictnessInterval = (
  analysisId,
  {
    candidateId,
    candidateIndex = 0,
    minStrictness = 0,
    maxStrictness = 1,
    step = 0.05,
  } = {},
) => asData(api.get(`/analyses/${analysisId}/strictness-interval`, {
  params: {
    candidate_id: candidateId,
    candidate_index: candidateIndex,
    min_strictness: minStrictness,
    max_strictness: maxStrictness,
    step,
  },
}));

export const getStrictnessPreview = (
  analysisId,
  {
    candidateId,
    candidateIndex = 0,
    strictness = 0.5,
    maxTotal = 3,
  } = {},
) => asData(api.get(`/analyses/${analysisId}/strictness-preview`, {
  params: {
    candidate_id: candidateId,
    candidate_index: candidateIndex,
    strictness,
    max_total: maxTotal,
  },
}));

export const applyStrictnessPreview = (
  analysisId,
  {
    candidateId,
    candidateIndex = 0,
    strictness = 0.5,
    maxTotal = 3,
  } = {},
) => asData(api.post(`/analyses/${analysisId}/apply-strictness`, {
  candidate_id: candidateId,
  candidate_index: candidateIndex,
  strictness,
  max_total: maxTotal,
}));


export const restoreStrictnessStack = (analysisId) => asData(api.post(`/analyses/${analysisId}/restore-strictness-stack`));

export const updateAnalysisPresentationMode = (analysisId, presentationMode = 'default') => asData(api.post(`/analyses/${analysisId}/presentation-mode`, {
  presentation_mode: presentationMode,
}));
export const getFractalTree = (analysisId, { candidateId, candidateIndex = 0 } = {}) => asData(api.get(
  `/analyses/${analysisId}/fractal`,
  {
    params: {
      candidate_id: candidateId,
      candidate_index: candidateIndex,
    },
  },
));

export const zoomFractal = (
  analysisId,
  { path = [0], candidateId, candidateIndex = 0 } = {},
) => asData(api.post(`/analyses/${analysisId}/zoom`, {
  path,
  candidate_id: candidateId,
  candidate_index: candidateIndex,
}));

export const getPremiumReport = (
  analysisId,
  {
    candidateId,
    candidateIndex = 0,
    sections,
  } = {},
) => asData(api.get(`/analyses/${analysisId}/report`, {
  params: {
    candidate_id: candidateId,
    candidate_index: candidateIndex,
    sections,
  },
  responseType: 'blob',
}));

export const getWavePredictions = (
  analysisId,
  {
    candidateId,
    candidateIndex = 0,
    predictionSteps = 3,
    includeCorrection = true,
  } = {},
) => asData(api.get(`/analyses/${analysisId}/predict`, {
  params: {
    candidate_id: candidateId,
    candidate_index: candidateIndex,
    prediction_steps: predictionSteps,
    include_correction: includeCorrection,
  },
}));

export const getTimeframePrediction = (
  analysisId,
  {
    candidateId,
    candidateIndex = 0,
    method = 'all',
  } = {},
) => asData(api.get(`/analyses/${analysisId}/timeframe`, {
  params: {
    candidate_id: candidateId,
    candidate_index: candidateIndex,
    method,
  },
}));

export const getIndicatorsSnapshot = (
  analysisId,
  {
    candidateId,
    candidateIndex = 0,
  } = {},
) => asData(api.get(`/analyses/${analysisId}/indicators`, {
  params: {
    candidate_id: candidateId,
    candidate_index: candidateIndex,
  },
}));

export const getIntelligenceSnapshot = (
  analysisId,
  {
    candidateId,
    candidateIndex = 0,
  } = {},
) => asData(api.get(`/analyses/${analysisId}/intelligence`, {
  params: {
    candidate_id: candidateId,
    candidate_index: candidateIndex,
  },
}));

export const sendAnalysisChat = (
  analysisId,
  {
    messages,
    candidateId,
  } = {},
) => asData(api.post(`/analyses/${analysisId}/chat`, {
  messages,
  candidate_id: candidateId,
}));

export const exportAnalysis = (analysisId, format = 'json') => {
  const form = new FormData();
  form.append('format', format);
  return asData(api.post(`/analyses/${analysisId}/export`, form, { responseType: 'blob' }));
};

export const getSettings = () => asData(api.get('/settings'));
export const updateSettings = (data) => asData(api.put('/settings', data));
export const getRawKeys = () => asData(api.get('/settings/raw-keys'));

export const getVisionModels = () => asData(api.get('/models/vision'));
export const getThinkingModels = () => asData(api.get('/models/thinking'));

export const createScorerBatch = (name, mode, symbol, files) => {
  const form = new FormData();
  form.append('name', name);
  form.append('mode', mode);
  form.append('symbol', symbol);
  form.append('prediction_date', '');
  files.forEach((file) => form.append('files', file));
  return asData(api.post('/scorer/batches', form, {
    headers: { 'Content-Type': 'multipart/form-data' },
  }));
};

export const listScorerBatches = () => asData(api.get('/scorer/batches'));
export const getScorerBatch = (id) => asData(api.get(`/scorer/batches/${id}`));
export const getScorerPriceCheck = (symbol) => asData(api.get('/scorer/price-check', { params: { symbol } }));

export const exportScorerBatch = (batchId, format = 'csv') => {
  const form = new FormData();
  form.append('format', format);
  return asData(api.post(`/scorer/batches/${batchId}/export`, form, { responseType: 'blob' }));
};

export const getHealthDeep = () => asData(api.get('/health-deep'));
export const getTradingStatus = () => asData(api.get('/trading/status'));
export const toggleTradingEngine = (action) => asData(api.post('/trading/toggle', { action }));

export const submitUserAnnotation = (analysisId, payload) =>
  asData(api.post(`/analyses/${analysisId}/user-annotation`, payload));

export const getUserAnnotation = (analysisId) =>
  asData(api.get(`/analyses/${analysisId}/user-annotation`));

export const getAuditViolations = (analysisId, candidateId) =>
  asData(api.get(`/analyses/${analysisId}/audit`, {
    params: { candidate_id: candidateId },
  }));

// Data Pipeline (Lane A P0) — read-only status only. Dead PUT/test clients removed.
export const getPipelineStatus = () => asData(api.get('/pipeline/status'));

/** @deprecated alias — prefer getPipelineStatus */
export const getIngestionStatus = () => asData(api.get('/ingestion/status'));

// Learning API
export const getLearningStats = () => asData(api.get('/learning/stats'));

export const getLearningRules = () => asData(api.get('/learning/rules'));

export const triggerLearningAnalysis = () => asData(api.post('/learning/analyze'));

export const addManualRule = (category, ruleText) =>
  asData(api.post('/learning/rules/manual', { category, rule_text: ruleText }));

export const deactivateRule = (ruleId) =>
  asData(api.post(`/learning/rules/${ruleId}/deactivate`));

// Session / Terminal live endpoints
export const getLatestAnalysisSession = () =>
  asData(api.get('/session/latest-analysis'));

export const getLatestIntelligenceSession = () =>
  asData(api.get('/session/latest-intelligence'));

export const getTerminalSnapshot = (analysisId = null) =>
  asData(api.get('/terminal/snapshot', { params: analysisId ? { analysis_id: analysisId } : {} }));

// Optional alias once Lane A registers GET /terminal/macro (same payload shape as snapshot.macro).
// Tabs primarily consume snapshot.macro via useTerminalData — this is a thin convenience client.
export const getTerminalMacro = () =>
  asData(api.get('/terminal/macro'));

// Consensus Engine endpoints
export const getConsensusStatus = (analysisId) =>
  asData(api.get(`/analyses/${analysisId}/consensus-status`));

export const runConsensusAnalysis = (analysisId) =>
  asData(api.post(`/analyses/${analysisId}/run-consensus`));

// Admin Panel endpoints
export const getActivityLogs = ({ limit = 200, offset = 0, types, levels, analysisId, sinceHours } = {}) => {
  const params = new URLSearchParams();
  params.set('limit', String(limit));
  params.set('offset', String(offset));
  if (types) params.set('types', types);
  if (levels) params.set('levels', levels);
  if (analysisId) params.set('analysis_id', analysisId);
  if (sinceHours) params.set('since_hours', String(sinceHours));
  return asData(api.get(`/admin/activity-logs?${params.toString()}`));
};

export const getActivitySummary = (hours = 24) =>
  asData(api.get(`/admin/activity-summary?hours=${hours}`));

export const getScorecard = () => asData(api.get('/admin/scorecard'));

export const refreshScorecard = () => asData(api.post('/admin/scorecard/refresh'));

export const getLeaderboard = ({ sortBy = 'risk_reward_ratio', direction, minConfidence = 0, status = 'open', limit = 50 } = {}) => {
  const params = new URLSearchParams();
  params.set('sort_by', sortBy);
  params.set('min_confidence', String(minConfidence));
  params.set('status', status);
  params.set('limit', String(limit));
  if (direction) params.set('direction', direction);
  return asData(api.get(`/admin/leaderboard?${params.toString()}`));
};

export const getAllTrades = ({ analysisId, projectId, status, direction, minConfidence, symbol, limit = 500, offset = 0, sortBy = 'created_at' } = {}) => {
  const params = new URLSearchParams();
  params.set('limit', String(limit));
  params.set('offset', String(offset));
  params.set('sort_by', sortBy);
  if (analysisId) params.set('analysis_id', analysisId);
  if (projectId) params.set('project_id', projectId);
  if (status) params.set('status', status);
  if (direction) params.set('direction', direction);
  if (minConfidence !== undefined) params.set('min_confidence', String(minConfidence));
  if (symbol) params.set('symbol', symbol);
  return asData(api.get(`/admin/trades?${params.toString()}`));
};

export const exportTrades = ({ format = 'json', status, minConfidence } = {}) => {
  const params = new URLSearchParams();
  params.set('format', format);
  if (status) params.set('status', status);
  if (minConfidence !== undefined) params.set('min_confidence', String(minConfidence));
  return asData(api.get(`/admin/trades/export?${params.toString()}`));
};

export const resolveTrade = (tradeId, outcome, resolutionNote) =>
  asData(api.post(`/admin/trades/${tradeId}/resolve`, { outcome, resolution_note: resolutionNote }));

export const getConsensusTrace = (analysisId) =>
  asData(api.get(`/admin/consensus-trace/${analysisId}`));

export default api;




